import React, { useEffect, useContext, useRef, useState } from "react";
import {
  Routes,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Navigate,
  BrowserRouter,
} from "react-router-dom";
import { DashboardRoutes } from "./DashboardRoutes";
import LoginForm from "../components/login/LoginForm";
import styled from "styled-components";
import { connect } from "react-redux";
import {
  handleAutoLogin,
  hideFreesubscriptionModal,
  getDeviceInfo,
  startApp,
} from "../components/login/userSession.redux";
import {
  getPlugins,
  getPluginSubscriptions,
  getJobServices,
  getSalesPersons,
  getLocations,
} from "../components/plugins/plugin.redux";
import TailSpin from "../components/ui/Loader";
import { LoadingPage } from "../components/ui/LoadingPage";
import { BlockIphonePage } from "../components/ui/BlockIphonePage";
import { useTranslation } from "react-i18next";
import RotationIcon from "./phone-rotation-icon.gif";
import { ModalSession } from "../components/modal/ModalSession";
import { ModalWoocommerceMatchPicker } from "../components/modal/ModalWoocommerceMatchPicker";
import moment from "moment";
import { Payment } from "../components/payment/Payment";
import FeedbackModal from "../components/feedback/FeedbackModal";
import SubscriptionActivatingBanner from "../components/feedback/SubscriptionActivatingBanner";
import {
  captureOpenStoreRequest,
  consumeOpenStoreRequest,
  hasPendingOpenStore,
} from "./openStore";

// Antes que el router: /open-store no debe convertirse en /login sin dejar rastro.
captureOpenStoreRequest();

const ScreenContainer = styled.div`
  width: 100%;
  height: 100vh;
  height: -webkit-fill-available;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`;

const SpinnerContainer = styled.div`
  overflow: hidden;
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const UpgradeToProModal = (props) => {
  const {
    initialValues,
    subscription,
    subscriptionInfo,
    show,
    onHide,
  } = props;
  const { t } = useTranslation();

  if (!subscriptionInfo || !subscription) return null;

  const nextBilling =
    subscription.type.indexOf("PAYPAL") >= 0
      ? moment(subscriptionInfo.billing_info?.next_billing_time).format(
          "DD/MM/YYYY",
        )
      : subscription.type.indexOf("STRIPE") >= 0
      ? moment.unix(subscriptionInfo.current_period_end).format("DD/MM/YYYY")
      : "-";

  const status = subscriptionInfo.status?.toUpperCase();

  const message = (
    <div style={{ fontFamily: "Poppins", fontSize: 13, lineHeight: 1.5 }}>
      <div>
        <strong>{t("form.label.username")}: </strong>
        {initialValues?.username}
      </div>
      <div>
        <strong>{t("form.label.member_as")}: </strong>
        {moment(initialValues?.created_at).format("DD/MM/YYYY")}
      </div>
      <div>
        <strong>{t("form.label.next_billing")}: </strong>
        {nextBilling}
      </div>
      <div>
        <strong>{t("form.label.subscriptionStatus")}: </strong>
        <span>{status}</span>
      </div>
      <div style={{ marginTop: 16, fontWeight: 400, textAlign: "justify" }}>
        {t("freeSubscriptionModal.verifyAccountStatus1")}
        <a href="mailto:support@spectrebowling.com">
          support@spectrebowling.com
        </a>
        {t("freeSubscriptionModal.verifyAccountStatus2")}
      </div>
    </div>
  );

  return (
    <FeedbackModal
      show={show}
      onHide={onHide}
      severity="warning"
      title={t("freeSubscriptionModal.statusTitle")}
      message={message}
      secondaryAction={{
        label: t("freeSubscriptionModal.close"),
        onClick: onHide,
      }}
      primaryAction={{
        label: t("freeSubscriptionModal.accountSettings"),
        onClick: () => {
          onHide();
          // React Router navigation — no-op if already on /profile, no full
          // page reload either way. (window.location.assign always reloads.)
          mainRouter.navigate("/profile");
        },
        variant: "success",
      }}
    />
  );
};

export const mainRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<DashboardRoutes />}>
      <Route path="*" element={<DashboardRoutes />} />
    </Route>
  )
);

const AppRouter = ({
  isAuthenticated,
  isSessionLoaded,
  handleAutoLogin,
  showFreesubscriptionModal,
  hideFreesubscriptionModal,
  getDeviceInfo,
  isPhone,
  isLandscape,
  startApp,
  isBlockIphone,
  getPlugins,
  getPluginSubscriptions,
  getJobServices,
  getSalesPersons,
  getLocations,
  subscriptionInfo,
  takingPhoto,
  initialValues,
  subscription,
  readingBarcode,
}) => {
  const windowSize = useRef([window.innerWidth, window.innerHeight]);
  const { t } = useTranslation();
  // console.log("isAuthenticated ->", isAuthenticated);

  useEffect(() => {
    const handleOrientationChange = () => {
      // alert(`orientation change getDevice pre ${window.takingPhoto}`);
      if (!window.takingPhoto) {
        // alert("orientation change getDevice post");
        setTimeout(() => {
          // alert("orientation change getDevice");
          getDeviceInfo();
        }, 50);
      }
    };

    getDeviceInfo();
    if (window) {
      window.addEventListener("orientationchange", handleOrientationChange);
      return () => {
        window.removeEventListener(
          "orientationchange",
          handleOrientationChange
        );
      };
    }
  }, []);

  //useEffect to get the plugin list from the redux state
  useEffect(() => {
    if (isSessionLoaded && isAuthenticated) {
      getJobServices();
      getSalesPersons();
      getLocations();
    }
  }, [isSessionLoaded, isAuthenticated]);

  // Volver a la tienda SpectrePOS que mandó aquí (ver openStore.js).
  // Mientras se vuelve, no se pinta la app: quien viene de su tienda no pidió
  // ver el tablero de Spectre, y verlo un instante antes de saltar parece un
  // error. Si el enlace no sale, se muestra la app como siempre.
  const [openingStore, setOpeningStore] = useState(hasPendingOpenStore);
  useEffect(() => {
    if (!(isSessionLoaded && isAuthenticated)) return;
    consumeOpenStoreRequest().then((redirecting) => {
      if (!redirecting) setOpeningStore(false);
    });
  }, [isSessionLoaded, isAuthenticated]);

  useEffect(() => {
    if (subscriptionInfo.customer || subscriptionInfo.customer_id) {
      getPlugins();
      getPluginSubscriptions();
    }
  }, [subscriptionInfo]);

  useEffect(() => {
    if (!isSessionLoaded) {
      handleAutoLogin();
    }
  }, []);

  useEffect(() => {
    startApp();
  }, []);

  if (isPhone && isLandscape) {
    return (
      <ScreenContainer>
        <SpinnerContainer>
          <img style={{ width: "15vw" }} src={RotationIcon} />
        </SpinnerContainer>
      </ScreenContainer>
    );
  }

  if (isBlockIphone) {
    return (
      <ScreenContainer>
        <SpinnerContainer>
          <BlockIphonePage />
        </SpinnerContainer>
      </ScreenContainer>
    );
  }

  if (isSessionLoaded && isAuthenticated && openingStore) {
    return (
      <ScreenContainer>
        <SpinnerContainer>
          <LoadingPage />
          <TailSpin />
        </SpinnerContainer>
      </ScreenContainer>
    );
  }

  if (isSessionLoaded) {
    if (isAuthenticated) {
      return (
        <ScreenContainer>
          <RouterProvider router={mainRouter} />
          <UpgradeToProModal
            initialValues={initialValues}
            subscription={subscription}
            subscriptionInfo={subscriptionInfo}
            show={showFreesubscriptionModal}
            onHide={() => {
              hideFreesubscriptionModal();
            }}
            onUpgrade={() => {
              hideFreesubscriptionModal();
            }}
          />
          <ModalSession />
          <ModalWoocommerceMatchPicker />
          <SubscriptionActivatingBanner />
        </ScreenContainer>
      );
    } else {
      return (
        <ScreenContainer>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<LoginForm register={true} />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
        </ScreenContainer>
      );
    }
  } else {
    return (
      <ScreenContainer>
        <SpinnerContainer>
          <LoadingPage />
          <TailSpin />
        </SpinnerContainer>
      </ScreenContainer>
    );
  }
};

export default connect(
  (state) => ({
    isAuthenticated: state.userSession.logged,
    isSessionLoaded: state.userSession.isSessionLoaded,
    showFreesubscriptionModal: state.userSession.showFreesubscriptionModal,
    isPhone: state.userSession.deviceInfo.isMobileDevice,
    isIOS: state.userSession.deviceInfo.isIOS,
    isLandscape: !state.userSession.deviceInfo.isPortrait,
    isBlockIphone: state.userSession.deviceInfo.isBlockIphone,
    subscriptionInfo: state.userSession.subscriptionInfo,
    takingPhoto: state.userSession.takingPhoto,
    initialValues: state.userSession.userData,
    subscription: state.userSession.subscription,
    readingBarcode: state.balls.readingBarcode,
  }),
  {
    handleAutoLogin,
    hideFreesubscriptionModal,
    getDeviceInfo,
    startApp,
    getPlugins,
    getPluginSubscriptions,
    getJobServices,
    getSalesPersons,
    getLocations,
  }
)(AppRouter);
